import React from 'react'
import styled, { css } from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Interface
import { TextProps } from 'components/flex/Text/TextShell'

const ColumnTitleDark = styled.h2`
  color: ${({ theme }) => theme.color.light};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  margin-bottom: 1.5rem;

  @media (min-width: 992px) {
    font-size: 36px;
    line-height: 1.25;
  }

  @media (max-width: 991.98px) {
    font-size: 26px;
    line-height: 1.3;
  }
`

const ColumnTitleLight = styled.h2`
  color: ${({ theme }) => theme.color.primary};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  margin-bottom: 1.5rem;

  @media (min-width: 992px) {
    font-size: 36px;
    line-height: 1.25;
  }

  @media (max-width: 991.98px) {
    font-size: 26px;
    line-height: 1.3;
  }
`

const columnBodyBase = css`
  font-family: ${({ theme }) => theme.font.family.primary};
  font-size: ${({ theme }) => theme.font.size.medium};
  line-height: 1.5;

  & p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & h2 {
    font-size: 36px;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 35px;
  }

  & ul {
    list-style: none;
    padding-left: 0;
    margin: 0 0 1rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & li {
    position: relative;
    padding-left: 1.25rem;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: '>';
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.color.secondary};
      font-weight: ${({ theme }) => theme.font.weight.bold};
    }
  }
`

const ColumnBodyDark = styled(ParseContent)`
  color: ${({ theme }) => theme.color.light};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  ${columnBodyBase}

  & a {
    color: ${({ theme }) => theme.color.secondary};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    &:hover {
      color: ${({ theme }) => theme.color.light};
    }
  }
`

const ColumnBodyLight = styled(ParseContent)`
  color: ${({ theme }) => theme.color.primary};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  ${columnBodyBase}

  & a {
    color: ${({ theme }) => theme.color.dark};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    &:hover {
      color: ${({ theme }) => theme.color.secondary};
    }
  }
`

const ColumnBlockDark = styled.div`
  background-color: ${({ theme }) => theme.color.primary};
  padding: 2rem;
  height: 100%;
`

const ColumnBlockLight = styled.div`
  background-color: ${({ theme }) => theme.color.light};
  padding: 2rem;
  height: 100%;
`

const TextTwoColumns: React.FC<TextProps> = ({ fields }) => {
  const leftTitle = fields.columns?.[0]?.title || ''
  const leftHtml = fields.columns?.[0]?.description || fields.description || ''
  const rightTitle = fields.columns?.[1]?.title || ''
  const rightHtml =
    fields.columns?.[1]?.description || fields.descriptionSecond || ''

  const hasLeft = Boolean(
    (leftTitle && String(leftTitle).trim()) ||
      (leftHtml && String(leftHtml).trim())
  )
  const hasRight = Boolean(
    (rightTitle && String(rightTitle).trim()) ||
      (rightHtml && String(rightHtml).trim())
  )

  if (!hasLeft && !hasRight) {
    return null
  }

  const colClass = hasLeft && hasRight ? 'col-lg-6' : 'col-12'

  return (
    <section>
      <div className="container py-3 py-lg-5">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div className="row g-4">
              {hasLeft ? (
                <div className={`${colClass}`}>
                  <ColumnBlockDark>
                    {leftTitle ? (
                      <ColumnTitleDark>{leftTitle}</ColumnTitleDark>
                    ) : null}
                    {leftHtml ? <ColumnBodyDark content={leftHtml} /> : null}
                  </ColumnBlockDark>
                </div>
              ) : null}
              {hasRight ? (
                <div className={`${colClass}`}>
                  <ColumnBlockLight>
                    {rightTitle ? (
                      <ColumnTitleLight>{rightTitle}</ColumnTitleLight>
                    ) : null}
                    {rightHtml ? <ColumnBodyLight content={rightHtml} /> : null}
                  </ColumnBlockLight>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TextTwoColumns
